/**
 * ExportPdfButton.tsx — the user-facing "Export PDF" control for the tract one-pager (T-258).
 *
 * All the real work is delegated to the pure, tested layers: fetch nearby stats →
 * buildExportForTract → downloadOnePager. The button only manages the click, a busy state, and a
 * graceful failure (a failed render logs and re-enables, never a stuck spinner).
 *
 * T-451 (revised): the site-locator schematic was removed from the one-pager, so this control no
 * longer reads the live map to capture geometry — its only remaining input from the map view is the
 * site pin, passed in as a prop.
 */
import { useState } from 'react'
import { Download } from 'lucide-react'
import { downloadOnePagerV2 } from './downloadOnePager'
import { buildExportForTractV2 } from '../../lib/pdf/onePagerExport'
import { fetchNearbyStats } from '../../lib/pdf/fetchNearby'
import { fetchSiteClassificationWithInputs } from '../../lib/pdf/fetchClassification'
import { DATA_VINTAGE, currentYear } from '../../lib/pdf/exportConfig'
import type { CensusTract, SiteScoreResult } from '../../lib/tractModel'
import type { StatePercentiles } from '../../lib/pdf/onePagerModelV2'

/** Cache the percentiles file so we only fetch it once per session. */
let percentilesCache: Record<string, StatePercentiles> | null = null

async function loadPercentiles(stateLabel: string): Promise<StatePercentiles | null> {
  try {
    if (!percentilesCache) {
      const res = await fetch('/data/station_percentiles.json')
      if (!res.ok) return null
      const data = await res.json()
      percentilesCache = {}
      for (const [st, vals] of Object.entries(data as Record<string, unknown>)) {
        const v = vals as Record<string, unknown>
        percentilesCache[st] = {
          stateLabel: st,
          stationCount: v.stationCount as number,
          percentiles: v.percentiles as number[],
          site_lvi: v.site_lvi as number[],
          site_cdpi: v.site_cdpi as number[],
          site_cmpi: v.site_cmpi as number[],
          gap_score: v.gap_score as number[],
          site_ppi: v.site_ppi as number[],
        }
      }
    }
    return percentilesCache[stateLabel] ?? null
  } catch {
    return null
  }
}

export interface ExportPdfButtonProps {
  tract: CensusTract
  /** The site address: a searched (exact) address, or a map-click reverse-geocoded (approximate)
   *  one. Null for a tract with neither (header degrades to GEOID). */
  address?: string | null
  /** T-478 — true when `address` came from reverse-geocoding a map click (approximate), so the
   *  one-pager prints an "approximate — based on map location" note beside it. */
  addressApproximate?: boolean
  /** State label for header + filename (app convention: the abbreviation, e.g. "GA"). */
  stateLabel: string
  /** Current site pin, if any — the origin the nearby competition/amenities are measured from. */
  pin?: { lat: number; lng: number } | null
  /** The site score already loaded for this pin. T-451 reads its nearby-POI set for the PDF's
   *  nearby block, so the export does not re-fetch POIs. Null in the tract-only (no-pin) state. */
  siteScore?: SiteScoreResult | null
}

export function ExportPdfButton({
  tract,
  address = null,
  addressApproximate = false,
  stateLabel,
  pin,
  siteScore = null,
}: ExportPdfButtonProps) {
  const [busy, setBusy] = useState(false)

  async function onExport() {
    if (busy) return
    setBusy(true)
    try {
      // T-451 / T-498: both the nearby block and the classification are measured FROM THE SITE PIN,
      // so they are gathered only when there is one (a tract opened from the map has no pin, and the
      // tract centroid is not the customer's site). They are independent lookups, so fetch them in
      // parallel — the export waits on the slower of the two, not their sum. Each degrades to null on
      // its own (the document prints an honest one-line explanation in place of a missing block).
      const [nearby, classResult, statePercentiles] = await Promise.all([
        pin && siteScore?.amenities?.nearbyPois
          ? fetchNearbyStats(
              pin.lat,
              pin.lng,
              siteScore.amenities.nearbyPois,
              siteScore.amenities.poiFetched ?? siteScore.amenities.nearbyPois.length,
            )
          : Promise.resolve(null),
        pin ? fetchSiteClassificationWithInputs(pin.lat, pin.lng) : Promise.resolve(null),
        loadPercentiles(stateLabel),
      ])
      const bundle = buildExportForTractV2({
        tract,
        address,
        addressApproximate,
        stateLabel,
        dataVintage: DATA_VINTAGE,
        year: currentYear(),
        nearby,
        siteScore,
        classification: classResult?.classification ?? null,
        blockInputs: classResult?.blockInputs ?? null,
        blockGroupInputs: classResult?.blockGroupInputs ?? null,
        statePercentiles: statePercentiles ?? undefined,
      })
      await downloadOnePagerV2(bundle)
    } catch (e) {
      // A failed PDF render must not wedge the button — log and re-enable.
      console.error('[pdf-export] export failed', e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      onClick={onExport}
      disabled={busy}
      aria-label="Export this tract as a PDF one-pager"
      className="flex w-full items-center justify-between
                 min-h-[44px] mt-2 border-t border-border/40 pt-2 px-0.5
                 text-xs text-muted-foreground hover:text-primary
                 cursor-pointer bg-transparent outline-none
                 focus-visible:ring-2 focus-visible:ring-ring rounded-sm
                 transition-colors disabled:opacity-60 disabled:cursor-wait"
    >
      <span>{busy ? 'Preparing PDF…' : 'Export PDF'}</span>
      <Download size={14} aria-hidden="true" />
    </button>
  )
}
